import React, { useState } from 'react';
import { FieldProps } from '../../../form/field/Field';
import { useIntl } from 'react-intl';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';
import { selectLocale } from '@/store/selectors/commonSelectors';
import { Button } from '../../../buttons/Button';
import classNames from 'classnames';
import PasswordField from '@/components/form/password/PasswordField';
import Form from '@/components/form/Form';
import { useBffApiRequest } from '@/hooks/useBffApiRequest';
import BffApiService from '@/shared/bffApiService';
import FieldValidation from '@/components/form/field-validation/FieldValidation';
import { RegisterValidationRules } from '@/constants/constants';
import { BffErrorCode } from '@lucky7ventures/bff-types';

interface PasswordChangeProps extends Omit<FieldProps, 'label' | 'name'> {
  onSuccess?: () => void;
}

const PasswordChangeForm = (props: PasswordChangeProps) => {
  const { onSuccess, ...rest } = props;
  const intl = useIntl();
  const locale = useSelector(selectLocale);
  const isThailand = locale === 'th';
  const { request, loading, error } = useBffApiRequest();
  const [wrongPasswordError, setWrongPasswordError] = useState(false);

  const changePasswordSchema = Yup.object().shape({
    current: Yup.string()
      .min(6)
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .test('invalid current password', intl.formatMessage({ id: 'error.48' }), function () {
        return !wrongPasswordError;
      }),
    new: Yup.string()
      .trim()
      .required(intl.formatMessage({ id: 'input.error.required' }))
      .matches(
        isThailand ? RegisterValidationRules.password.Thai : RegisterValidationRules.password.Latin,
        intl.formatMessage({ id: 'inputs.password.valid.criteria' }),
      ),
    confirm: Yup.string()
      .oneOf([Yup.ref('new'), null], intl.formatMessage({ id: 'inputs.password.confirm.notSame' }))
      .required(intl.formatMessage({ id: 'input.error.required' })),
  });

  return (
    <Form
      className="w-full flex flex-col space-y-5"
      initialValues={{ new: '', current: '', confirm: '' }}
      validationSchema={changePasswordSchema}
      validateOnChange
      validateOnMount
      validateOnBlur
      onSubmit={(values, helpers) => {
        request({
          apiMethod: BffApiService.changePassword,
          payload: { currentPassword: values.current, newPassword: values.new },
          successCallback: () => {
            helpers.resetForm();
            onSuccess?.();
          },
          errorCallback: bffError => {
            if (bffError?.code === BffErrorCode.GIG_INVALID_PASSWORD) {
              setWrongPasswordError(true);
              helpers.validateField('current');
            }
          },
        });
      }}
    >
      {({ isValid, setValues, values, handleChange }) => {
        return (
          <>
            <PasswordField
              name="current"
              id="current_id"
              onChange={e => {
                setWrongPasswordError(false);
                handleChange(e);
              }}
              label={intl.formatMessage({ id: 'inputs.password.placeholder' })}
              {...rest}
            />
            <PasswordField
              name="new"
              id="new_id"
              onBlurCapture={() => setValues({ ...values, new: values.new.trim() })}
              label={intl.formatMessage({ id: 'inputs.password.new' })}
              {...rest}
            />
            <FieldValidation className="-mt-5 !text-blue-medium">
              <FieldValidation.Criteria
                isValid={new RegExp(/^(?!.*\s).{8,25}$/).test(values.new)}
                message={intl.formatMessage({ id: 'inputs.password.length.criteria' })}
              />
              <FieldValidation.Criteria
                isValid={new RegExp(/[0-9]/).test(values.new)}
                message={intl.formatMessage({ id: 'inputs.password.numbers.criteria' })}
              />
              {isThailand && (
                <FieldValidation.Criteria
                  isValid={new RegExp('(?=.*\\p{L})', 'u').test(values.new)}
                  message={intl.formatMessage({
                    id: 'inputs.password.letters.criteria',
                  })}
                />
              )}
              {!isThailand && (
                <>
                  <FieldValidation.Criteria
                    isValid={new RegExp(/[a-z]/).test(values.new)}
                    message={intl.formatMessage({
                      id: 'inputs.password.lowercaseLetters.criteria',
                    })}
                  />
                  <FieldValidation.Criteria
                    isValid={new RegExp(/[A-Z]/).test(values.new)}
                    message={intl.formatMessage({
                      id: 'inputs.password.uppercaseLetters.criteria',
                    })}
                  />
                </>
              )}
            </FieldValidation>
            <PasswordField
              name="confirm"
              id="confirm_id"
              onBlurCapture={() => setValues({ ...values, confirm: values.confirm.trim() })}
              label={intl.formatMessage({ id: 'inputs.password.confirm' })}
              {...rest}
            />
            <div className="flex flex-col space-y-4 sm:space-y-0 sm:flex-row justify-between mt-3 items-center">
              <div className={classNames('text-sm w-3/5', 'text-red-light')}>
                {error &&
                  error.code !== BffErrorCode.GIG_INVALID_PASSWORD &&
                  `${intl.formatMessage({ id: 'error.support' })} [${error.code}]`}
              </div>
              <div className="w-full sm:w-auto">
                <Button
                  data-cy="change-password-submit-button"
                  disabled={!isValid}
                  type="submit"
                  text={intl.formatMessage({ id: 'settings.details.password.submitButton' })}
                  isLoading={loading}
                />
              </div>
            </div>
          </>
        );
      }}
    </Form>
  );
};

export default PasswordChangeForm;
